import type { CommandHistory } from './commandHistory.js';
import {
  createAddExistingInstanceCommand,
  createAddFrameCommand,
  createAddInstanceCommand,
  createAddLayerCommand,
  createAttachLibraryCommand,
  createClearCommand,
  createConnectCommand,
  createCutEdgesCommand,
  createDeleteNodeCommand,
  createDeleteSceneNodeCommand,
  createDetachLibraryCommand,
  createLibraryItemCommand,
  createMoveNodeCommand,
  createNodeCommand,
  createReconnectEdgeCommand,
  createRenameComponentCommand,
  createRenameSceneNodeCommand,
  createSaveToLibraryCommand,
  createSetFramePropertyCommand,
  createSetInstancePropertyCommand,
  createSetNodeDataCommand,
  createReplaceNodeCommand,
  createSetNodeParamOverrideCommand,
  createSetParamValueCommand,
  createSetRuntimeGraphCommand,
  createSetDataOwnerCommand,
  createSetDataSourcesCommand,
  createSetShapesCommand,
  createSetRuntimeStateCommand,
  createSetRuntimeModelCommand,
  createSetSceneEventsCommand,
  createSetSceneDataContextCommand,
  type AddExistingInstanceCmd,
  type AddFrameCmd,
  type AddInstanceCmd,
  type AddLayerCmd,
  type CreateLibraryItemCmd,
  type CreateNodeCmd,
  type EditorCommand,
  type SaveToLibraryCmd,
} from './commandBuilders.js';
import type { EditorRuntime } from './editorRuntime.js';
import type { AppState, RuntimeGraphKind } from '../state/appState.js';
import { getNodeDataRecord, isValueNode } from '../graph/nodeDefs.js';
import { RENDER_NODE_DEFS } from '../graph/renderNodeDefs.js';
import type { GraphNode, ParamValue, ValuePortType, ValueSubtype } from '../graph/nodeDefs.js';
import type { FrameNode, SceneNodeId } from '../scene/sceneState.js';
import type { DataGraphDef, DataGraphInstance } from '../graph/dataGraph.js';
import type { EventGraphDef, RuntimeStateSchema, SceneEventBindings } from '../graph/eventGraph.js';

export type {
  EdgeRef,
  FrameEditableProps,
  InstanceEditableProps,
} from './commandBuilders.js';

export interface EditorCommandHooks {
  syncEditor?: () => boolean;
  commandHistory: Pick<CommandHistory, 'runCommandWithHistory'>;
}

export interface EditorCommands {
  addNode(nodeType: string, x: number, y: number): GraphNode | null;
  moveNode(nodeId: string, x: number, y: number): void;
  deleteNode(nodeId: string): void;
  setNodeData(nodeId: string, field: string, value: unknown): void;
  replaceNode(nodeId: string, node: GraphNode): void;
  setValueOutputType(nodeId: string, valueType: ValuePortType): void;
  setValueComponent(nodeId: string, component: 0 | 1 | 2 | 3, value: number): void;
  setValueSubtype(nodeId: string, subtype: ValueSubtype): void;
  setValueExposed(nodeId: string, exposed: boolean): void;
  setValueName(nodeId: string, name: string): void;
  setValueRangeField(nodeId: string, field: 'min' | 'max', value: number): void;
  setValueRangeEnabled(nodeId: string, field: 'min' | 'max', enabled: boolean): void;
  setNodeNumericInputValue(nodeId: string, inputName: string, value: number): void;
  connectEdge(edge: import('./commandBuilders.js').EdgeRef, isReconnect?: boolean): void;
  reconnectEdge(fromEdge: import('./commandBuilders.js').EdgeRef, toEdge: import('./commandBuilders.js').EdgeRef): void;
  disconnectEdges(edges: import('./commandBuilders.js').EdgeRef[]): void;
  setParamValue(key: string, value: ParamValue): void;
  /**
   * Set or clear one parameter override on a scene node. A node's override
   * wins over the layer's value when the frame is drawn, so this is what an
   * edit has to touch for an overridden node to move at all.
   */
  setNodeParamOverride(nodeId: SceneNodeId, key: string, value: ParamValue | undefined): void;
  addFrame(layout: 'row' | 'column' | 'stack' | null, parentId: SceneNodeId | null): FrameNode;
  addLayer(parentId: SceneNodeId | null): { nodeId: SceneNodeId; layerId: string } | null;
  addInstance(parentId: SceneNodeId | null): { instanceId: SceneNodeId; componentId: string };
  addExistingInstance(componentId: string, parentId: SceneNodeId | null): { instanceId: SceneNodeId; componentId: string } | null;
  createLibraryItem(kind: 'geometry' | 'material', name: string): { id: string };
  saveToLibrary(
    kind: 'geometry' | 'material',
    name: string,
    componentId: string | null,
    fromSlot: 'geometry' | 'material',
    layerId?: string,
  ): { id: string } | null;
  deleteSceneNode(nodeId: SceneNodeId): void;
  setFrameProperty<K extends keyof import('./commandBuilders.js').FrameEditableProps>(
    nodeId: SceneNodeId,
    prop: K,
    value: import('./commandBuilders.js').FrameEditableProps[K],
  ): void;
  setInstanceProperty<K extends keyof import('./commandBuilders.js').InstanceEditableProps>(
    nodeId: SceneNodeId,
    prop: K,
    value: import('./commandBuilders.js').InstanceEditableProps[K],
  ): void;
  renameSceneNode(nodeId: SceneNodeId, name: string): void;
  renameComponent(componentId: string, name: string): void;
  clearApp(): void;
  attachLibrary(componentId: string | null, slot: 'geometry' | 'material', libraryId: string, layerId?: string): void;
  detachLibrary(componentId: string | null, slot: 'geometry' | 'material', layerId?: string): void;
  setRuntimeGraph(kind: RuntimeGraphKind, graphId: string, graph: DataGraphDef | EventGraphDef | null): void;
  setDataOwner(owner: { type: 'document' } | { type: 'scene'; nodeId: SceneNodeId }, instance: DataGraphInstance | null): void;
  setDataSources(sources: AppState['dataSources']): void;
  setShapes(shapes: AppState['shapes']): void;
  setRuntimeState(schema: RuntimeStateSchema): void;
  setRuntimeModel(schema: RuntimeStateSchema, dataGraphs: Record<string, DataGraphDef>, eventGraphs: Record<string, EventGraphDef>): void;
  setSceneEvents(nodeId: SceneNodeId, events: SceneEventBindings | null): void;
  setSceneDataContext(nodeId: SceneNodeId, context: import('../graph/dataGraph.js').SceneDataContext | null): void;
}

function createDispatcher(
  runtime: EditorRuntime,
  commandHistory: Pick<CommandHistory, 'runCommandWithHistory'>,
) {
  function dispatch(cmd: CreateNodeCmd): GraphNode | null;
  function dispatch(cmd: AddFrameCmd): FrameNode;
  function dispatch(cmd: AddLayerCmd): { nodeId: SceneNodeId; layerId: string };
  function dispatch(cmd: AddInstanceCmd): { instanceId: SceneNodeId; componentId: string };
  function dispatch(cmd: AddExistingInstanceCmd): { instanceId: SceneNodeId; componentId: string };
  function dispatch(cmd: CreateLibraryItemCmd | SaveToLibraryCmd): { id: string };
  function dispatch(cmd: EditorCommand): void;
  function dispatch(cmd: EditorCommand) {
    return commandHistory.runCommandWithHistory(cmd, runtime.labelCommand(cmd), runtime.executeCommand);
  }
  return dispatch;
}

export function createEditorCommands(
  runtime: EditorRuntime,
  hooks: EditorCommandHooks,
): EditorCommands {
  const dispatch = createDispatcher(runtime, hooks.commandHistory);
  const appState = runtime.appState;

  const commands: EditorCommands = {
    addNode(nodeType, x, y) {
      const command = createNodeCommand(appState, nodeType, x, y);
      return command ? dispatch(command) : null;
    },

    moveNode(nodeId, x, y) {
      dispatch(createMoveNodeCommand(nodeId, x, y));
    },

    deleteNode(nodeId) {
      dispatch(createDeleteNodeCommand(nodeId));
    },

    setNodeData(nodeId, field, value) {
      dispatch(createSetNodeDataCommand(nodeId, field, value));
    },

    replaceNode(nodeId, node) {
      dispatch(createReplaceNodeCommand(nodeId, node));
    },

    setValueOutputType(nodeId, valueType) {
      const node = appState.nodes.find(candidate => candidate.id === nodeId);
      if (!node || node.type !== 'Value') return;
      commands.setNodeData(nodeId, 'valueType', valueType);
    },

    setValueComponent(nodeId, component, value) {
      const node = appState.nodes.find(candidate => candidate.id === nodeId);
      if (!node || !isValueNode(node)) return;
      commands.setNodeData(nodeId, `value${component}`, value);
    },

    setValueSubtype(nodeId, subtype) {
      const node = appState.nodes.find(candidate => candidate.id === nodeId);
      if (!node || !isValueNode(node)) return;
      commands.setNodeData(nodeId, 'subtype', subtype);
    },

    setValueExposed(nodeId, exposed) {
      const node = appState.nodes.find(candidate => candidate.id === nodeId);
      if (!node || !isValueNode(node)) return;
      commands.setNodeData(nodeId, 'exposed', exposed);
    },

    setValueName(nodeId, name) {
      const node = appState.nodes.find(candidate => candidate.id === nodeId);
      if (!node || !isValueNode(node)) return;
      commands.setNodeData(nodeId, 'name', name);
    },

    setValueRangeField(nodeId, field, value) {
      const node = appState.nodes.find(candidate => candidate.id === nodeId);
      if (!node || !isValueNode(node)) return;
      commands.setNodeData(nodeId, field, value);
    },

    setValueRangeEnabled(nodeId, field, enabled) {
      const node = appState.nodes.find(candidate => candidate.id === nodeId);
      if (!node || !isValueNode(node)) return;
      commands.setNodeData(nodeId, field === 'min' ? 'hasMin' : 'hasMax', enabled);
    },

    setNodeNumericInputValue(nodeId, inputName, value) {
      const node = appState.nodes.find(candidate => candidate.id === nodeId);
      if (!node) return;
      const def = RENDER_NODE_DEFS[node.type];
      if (!def) return;

      const inputs = typeof def.inputs === 'function' ? def.inputs(node) : def.inputs;
      const input = inputs.find(candidate => candidate.name === inputName);
      const knownNumericDefault = typeof def.defaults[inputName] === 'number';
      const existingNumericValue = typeof getNodeDataRecord(node)[inputName] === 'number';

      if (input?.type !== 'float' && !knownNumericDefault && !existingNumericValue) return;
      commands.setNodeData(nodeId, inputName, value);
    },

    connectEdge(edge, isReconnect = false) {
      dispatch(createConnectCommand(edge, isReconnect));
    },

    reconnectEdge(fromEdge, toEdge) {
      dispatch(createReconnectEdgeCommand(fromEdge, toEdge));
    },

    disconnectEdges(edges) {
      dispatch(createCutEdgesCommand(edges));
    },

    setParamValue(key, value) {
      dispatch(createSetParamValueCommand(key, value));
    },

    setNodeParamOverride(nodeId, key, value) {
      dispatch(createSetNodeParamOverrideCommand(nodeId, key, value));
    },

    addFrame(layout, parentId) {
      return dispatch(createAddFrameCommand(appState, layout, parentId));
    },

    addLayer(parentId) {
      const command = createAddLayerCommand(appState, parentId);
      return command ? dispatch(command) : null;
    },

    addInstance(parentId) {
      return dispatch(createAddInstanceCommand(appState, parentId));
    },

    addExistingInstance(componentId, parentId) {
      const command = createAddExistingInstanceCommand(appState, componentId, parentId);
      return command ? dispatch(command) : null;
    },

    createLibraryItem(kind, name) {
      return dispatch(createLibraryItemCommand(kind, name));
    },

    saveToLibrary(kind, name, componentId, fromSlot, layerId) {
      hooks.syncEditor?.();
      const command = createSaveToLibraryCommand(appState, kind, name, componentId, fromSlot, layerId);
      return command ? dispatch(command) : null;
    },

    deleteSceneNode(nodeId) {
      dispatch(createDeleteSceneNodeCommand(nodeId));
    },

    setFrameProperty(nodeId, prop, value) {
      dispatch(createSetFramePropertyCommand(nodeId, prop, value));
    },

    setInstanceProperty(nodeId, prop, value) {
      dispatch(createSetInstancePropertyCommand(nodeId, prop, value));
    },

    renameSceneNode(nodeId, name) {
      dispatch(createRenameSceneNodeCommand(nodeId, name));
    },

    renameComponent(componentId, name) {
      dispatch(createRenameComponentCommand(componentId, name));
    },

    clearApp() {
      dispatch(createClearCommand());
    },

    attachLibrary(componentId, slot, libraryId, layerId) {
      dispatch(createAttachLibraryCommand(componentId, slot, libraryId, layerId));
    },

    detachLibrary(componentId, slot, layerId) {
      dispatch(createDetachLibraryCommand(componentId, slot, layerId));
    },
    setRuntimeGraph(kind, graphId, graph) {
      dispatch(createSetRuntimeGraphCommand(kind, graphId, graph));
    },
    setDataOwner(owner, instance) {
      dispatch(createSetDataOwnerCommand(owner, instance));
    },
    setDataSources(sources) { dispatch(createSetDataSourcesCommand(sources)); },
    setShapes(shapes) { dispatch(createSetShapesCommand(shapes)); },
    setRuntimeState(schema) { dispatch(createSetRuntimeStateCommand(schema)); },
    setRuntimeModel(schema, dataGraphs, eventGraphs) { dispatch(createSetRuntimeModelCommand(schema, dataGraphs, eventGraphs)); },
    setSceneEvents(nodeId, events) { dispatch(createSetSceneEventsCommand(nodeId, events)); },
    setSceneDataContext(nodeId, context) { dispatch(createSetSceneDataContextCommand(nodeId, context)); },
  };

  return commands;
}
